import { Component, effect, inject, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    VoucherGridItemObject,
    VoucherNursingCareDiagnosisGridItemObject,
    VoucherNursingCareDiagnosisGridObject,
} from '@it2go/types';
import { VoucherDetailSituationDpStore } from './voucher-detail-situation-dp.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { filter } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { VoucherDetailService } from '../../../../pages/person/voucher-detail-page/voucher-detail-page.service';
import { OrganizationGlobalStore } from '../../../../../../store/organization.global-store';
import { enableFormControls, formAddText } from '../../../../../../utils/form.utils';
import { VoucherDetailSituationDpStore2 } from './voucher-detail-situation-dp.store2';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { PersonContactTypeEnum } from '@libs/types/src/services/enum/person-contact-type.enum';
import { personContactGridItemFrag } from '../../../../../../services/types/person.types';
import { FormatAddressPipe } from '../../../../../shared/pipes/format-address.pipe';
import {
    RadioYesNoInputComponent,
} from '../../../../../../components/kit/input/radio-input/radio-yes-no-input.component';
import { tableImports } from '../../../../../shared/components/kit/table/table.component';
import { RadioInputComponent } from '../../../../../../components/kit/input/radio-input/radio-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { sanitizeHtmlInput } from '@libs/shared/utils/sanitizer';
import { FolderGlobalStore } from '../../../../../../store/medicalService/folder.global-store';
import { UserChipRenderTypeEnum } from '../../../../../../components/kit/user-chip/user-chip.component.types';
import { Chip } from '@openng/optimus-ui/chip';
import { FormatPersonContactPipe } from '../../../../../shared/pipes/format-person-contact.pipe';

@Component({
    selector: 'app-voucher-detail-situation-dp',
    templateUrl: 'voucher-detail-situation-dp.component.html',
    styleUrls: ['voucher-detail-situation-dp.component.scss'],
    providers: [VoucherDetailSituationDpStore, VoucherDetailSituationDpStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslatePipe,
        FormatAddressPipe,
        RadioYesNoInputComponent,
        tableImports,
        RadioInputComponent,
        TextInputComponent,
        InputWysiwygComponent,
        Chip,
        FormatPersonContactPipe,
    ],
})
export class VoucherDetailSituationDpComponent {
    protected readonly store = inject(VoucherDetailSituationDpStore);
    private readonly translate = inject(TranslateService);
    private readonly detailService = inject(VoucherDetailService);


    public readonly organizationStore = inject(OrganizationGlobalStore);
    public readonly store2 = inject(VoucherDetailSituationDpStore2);
    protected readonly folderStore = inject(FolderGlobalStore);

    public readonly wysiwygs = viewChildren<InputWysiwygComponent>(InputWysiwygComponent);

    form!: FormGroup;

    partialOptions = [
        { name: 'voucher.situationDP.mobility.optionFull', value: true },
        { name: 'voucher.situationDP.mobility.optionPartial', value: false },
    ];

    constructor() {
        this.form = this.detailService.situationDPForm;

        effect(() => {
            enableFormControls(this.form, this.folderStore.canEdit());
        });

        effect(() => {
            const filter = new FilterBuilder();
            filter.where('folderId', this.store2.folderId());
            filter.where('type', PersonContactTypeEnum.CONTACT);

            this.store2.api.getPersonContacts(filter.singlePage(), personContactGridItemFrag).then();
        });
    }

    async addMedication() {
        const data = await this.detailService.formatActualMedicationsText();

        formAddText(this.form, 'medication', `<p>${sanitizeHtmlInput(data)}</p>`);
    }


    addDiagnosis() {
        this.detailService
            .voucherOnce()
            .pipe(
                filter((v) => !!v),
                switchMap((voucher: VoucherGridItemObject | null) => {
                    return this.store.nursingCareSvc.getNursingCareDiagnoses(
                        FilterBuilder.filter({
                            voucherNursingCareId: voucher!.id,
                        }),
                    );
                }),
            )
            .subscribe((d: VoucherNursingCareDiagnosisGridObject) => {
                const diagnoses: string [] = [];
                d?.['items'].forEach((it: VoucherNursingCareDiagnosisGridItemObject) => {
                    diagnoses.push(it.title || '');
                });

                let data = '';
                if (diagnoses.length) {
                    data = `${this.translate.instant('voucher.messages.actualDiagnoses')}:<br />${diagnoses.join('<br />')}`;
                } else {
                    data = this.translate.instant('voucher.messages.noDiagnoses');
                }

                this.form.patchValue({
                    goal: `${this.form.value.goal ? `${sanitizeHtmlInput(this.form.value.goal)}<br />` : ''}<p>${data}</p>`,
                });
            });
    }

    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
